import React from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';

import type { CustomStep, CustomStepType } from '@shared/patient-intake-status';

import { getStatusColour } from '../../utils';
import { ActionPrompt } from '../../components';
import { getStepConfig } from '../../getStepConfig';
import { usePatientIntakeStatusContext } from '../../hooks';
import type { BaseActionPromptProps } from './CustomSteps.types.ts';

type ColouredAlertProps = {
  colour: ReturnType<typeof getStatusColour>['colour'];
  children: React.ReactNode;
};

const ColouredAlert = ({ colour, children }: ColouredAlertProps) => {
  const theme = useTheme();

  if (colour === 'inherit') {
    return null;
  }

  const backgroundColor = alpha(theme.palette[colour].main, 0.1);

  return <Box sx={{ backgroundColor, p: 1, borderRadius: 1 }}>{children}</Box>;
};

type ActionDescriptionProps = {
  step: CustomStep[CustomStepType];
};

const ActionDescription = ({ step }: ActionDescriptionProps) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();
  const stepConfig = getStepConfig(step.type);

  const currentStatus = step.currentStatus?.status ?? 'NOT_STARTED';

  const { context } = stepConfig.statuses[currentStatus];

  const stepContext = context({ isHealthcareProvider });

  const { colour } = getStatusColour(currentStatus);

  if (currentStatus === 'NOT_STARTED' || currentStatus === 'COMPLETED') {
    return null;
  }

  return (
    <ColouredAlert colour={colour}>
      <Typography variant="body2">{stepContext}</Typography>
    </ColouredAlert>
  );
};

interface CustomStepActionPromptProps<TCustomStepType extends CustomStepType> extends BaseActionPromptProps {
  step: CustomStep[TCustomStepType];
  description: React.ReactNode;
  onClick: () => void;
}

export const CustomStepActionPrompt: React.FC<CustomStepActionPromptProps<CustomStepType>> = ({
  step,
  description,
  ...props
}) => (
  <ActionPrompt step={step} title={step.title} description={description} {...props}>
    <ActionDescription step={step} />
  </ActionPrompt>
);
